<template>
  <v-container fluid grid-list-xl class="pt-0 invoices-page">
    <!-- Toast notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      top
    >
      {{ snackbar.message }}
      <v-btn color="#999" dark @click="snackbar.show = false">Close</v-btn>
    </v-snackbar>

    <div class="inv-page-shell" :class="{ 'panel-open': detailDialog }">
      <div class="inv-main-content">
        <v-layout wrap>
      <!-- Page header -->
      <v-flex xs12 class="pt-0 pb-0">
        <div class="inv-page-header">
          <div>
            <h2 class="inv-title">AI Invoices</h2>
            <p class="inv-subtitle">
              Manage and track AI usage invoices by billing period
            </p>
          </div>
          <v-btn
            color="#4caf50"
            dark
            small
            @click="loadInvoices"
            :loading="loading"
          >
            <v-icon left small>mdi-refresh</v-icon>
            Refresh
          </v-btn>
        </div>
      </v-flex>

      <!-- Month / Year filter bar -->
      <v-flex xs12>
        <v-card class="inv-filter-bar pa-3 mb-1 elevation-1">
          <v-layout row wrap align-center>
            <v-flex xs12 sm4 md3>
              <v-select
                v-model="filterYear"
                :items="availableYears"
                label="Year"
                hide-details
                clearable
                class="inv-filter-select"
              ></v-select>
            </v-flex>
            <v-flex xs12 sm4 md3>
              <v-select
                v-model="filterMonth"
                :items="monthOptions"
                item-text="label"
                item-value="value"
                label="Month"
                hide-details
                clearable
                class="inv-filter-select"
              ></v-select>
            </v-flex>
            <v-flex xs12 sm4 md3>
              <v-btn flat small color="grey darken-1" @click="clearFilter">
                <v-icon left small>mdi-close-circle-outline</v-icon>
                Clear Filter
              </v-btn>
            </v-flex>
            <v-flex xs12 sm12 md3 class="text-xs-right">
              <span class="inv-result-count">
                {{ filteredInvoices.length }}
                invoice{{ filteredInvoices.length !== 1 ? "s" : "" }}
                <template v-if="filterYear || filterMonth">
                  in period</template
                >
              </span>
            </v-flex>
          </v-layout>
        </v-card>
      </v-flex>

      <!-- ── KPI Cards ──────────────────────────────────────────────── -->

      <!-- Loading skeletons -->
      <template v-if="loading">
        <v-flex v-for="n in 6" :key="'sk' + n" xs12 sm6 md4 lg2>
          <div class="kpi-card kpi-skeleton">
            <div class="skel-icon"></div>
            <div class="skel-val"></div>
            <div class="skel-lbl"></div>
          </div>
        </v-flex>
      </template>

      <!-- Populated KPIs -->
      <template v-if="!loading">
        <v-flex xs12 sm6 md4 lg2>
          <div class="kpi-card kpi-blue">
            <v-icon class="kpi-icon" color="white">mdi-file-document</v-icon>
            <div class="kpi-value">{{ kpis.count }}</div>
            <div class="kpi-label">Invoices</div>
          </div>
        </v-flex>
        <v-flex xs12 sm6 md4 lg2>
          <div class="kpi-card kpi-purple">
            <v-icon class="kpi-icon" color="white">mdi-phone</v-icon>
            <div class="kpi-value">{{ formatCurrency(kpis.twilio) }}</div>
            <div class="kpi-label">Twilio</div>
          </div>
        </v-flex>
        <v-flex xs12 sm6 md4 lg2>
          <div class="kpi-card kpi-teal">
            <v-icon class="kpi-icon" color="white">mdi-chip</v-icon>
            <div class="kpi-value">{{ formatCurrency(kpis.api) }}</div>
            <div class="kpi-label">API Cost</div>
          </div>
        </v-flex>
        <v-flex xs12 sm6 md4 lg2>
          <div class="kpi-card kpi-orange">
            <v-icon class="kpi-icon" color="white">mdi-cash</v-icon>
            <div class="kpi-value">{{ formatCurrency(kpis.total) }}</div>
            <div class="kpi-label">Total Billed</div>
          </div>
        </v-flex>
        <v-flex xs12 sm6 md4 lg2>
          <div class="kpi-card kpi-green">
            <v-icon class="kpi-icon" color="white">mdi-check-circle</v-icon>
            <div class="kpi-value">{{ formatCurrency(kpis.paid) }}</div>
            <div class="kpi-label">Paid</div>
          </div>
        </v-flex>
        <v-flex xs12 sm6 md4 lg2>
          <div class="kpi-card kpi-red">
            <v-icon class="kpi-icon" color="white">mdi-alert-circle</v-icon>
            <div class="kpi-value">{{ formatCurrency(kpis.outstanding) }}</div>
            <div class="kpi-label">Outstanding</div>
          </div>
        </v-flex>
      </template>

      <!-- ── Invoice Table ──────────────────────────────────────────── -->
      <v-flex xs12 class="pt-0 mt-2">
        <material-card color="#4caf50" class="mt-4" title="Invoices">
          <!-- Loading spinner -->
          <div v-if="loading" class="text-xs-center py-5">
            <v-progress-circular
              indeterminate
              color="#4caf50"
              size="48"
            ></v-progress-circular>
            <div class="mt-3 grey--text">Loading invoices…</div>
          </div>

          <!-- Error state -->
          <div v-else-if="loadError" class="text-xs-center py-5">
            <v-icon x-large color="error">mdi-alert-circle</v-icon>
            <div class="title mt-2">Failed to load invoices</div>
            <div class="grey--text mt-1 caption">{{ loadError }}</div>
            <v-btn color="#4caf50" dark class="mt-3" @click="loadInvoices"
              >Try Again</v-btn
            >
          </div>

          <!-- Empty state -->
          <div
            v-else-if="filteredInvoices.length === 0"
            class="text-xs-center py-5"
          >
            <v-icon x-large color="grey lighten-1"
              >mdi-file-document-outline</v-icon
            >
            <div class="title grey--text mt-2">No invoices found</div>
            <div
              class="grey--text caption mt-1"
              v-if="filterYear || filterMonth"
            >
              Try a different month or clear the filter.
            </div>
            <div class="grey--text caption mt-1" v-else>
              No invoices have been created yet.
            </div>
          </div>

          <!-- Data table -->
          <div v-else>
            <v-data-table
              :headers="tableHeaders"
              :items="paginatedInvoices"
              hide-actions
              disable-initial-sort
              class="inv-table"
            >
              <template slot="headerCell" slot-scope="{ header }">
                <span
                  class="font-weight-light text--darken-3"
                  v-text="header.text"
                />
              </template>

              <template slot="items" slot-scope="{ item }">
                <tr class="inv-row">
                  <td class="font-weight-medium">#{{ item.invoiceNumber }}</td>
                  <td class="nowrap">{{ getCallerDisplay(item) }}</td>
                  <td class="nowrap caption">
                    {{ formatPeriod(item.periodStart, item.periodEnd) }}
                  </td>
                  <td class="text-xs-center">{{ item.questionCount }}</td>
                  <td class="text-xs-right">
                    {{ formatCurrency(item.twilioCost) }}
                  </td>
                  <td class="text-xs-right">
                    {{ formatCurrency(item.apiCost) }}
                  </td>
                  <td class="text-xs-right font-weight-medium">
                    {{ formatCurrency(item.totalCost) }}
                  </td>
                  <td>
                    <v-chip
                      small
                      :color="getPayColor(item)"
                      text-color="white"
                      class="pay-chip"
                      >{{ getPayLabel(item) }}</v-chip
                    >
                  </td>
                  <td class="text-xs-right">
                    <span
                      v-if="
                        item.paidAmount !== null &&
                          item.paidAmount !== undefined
                      "
                    >
                      {{ formatCurrency(item.paidAmount) }}
                    </span>
                    <span v-else class="grey--text">—</span>
                  </td>
                  <td class="nowrap">
                    <v-btn
                      icon
                      small
                      @click.stop="openDetail(item)"
                      title="View Details"
                    >
                      <v-icon small color="#4caf50">mdi-eye</v-icon>
                    </v-btn>
                    <v-btn
                      v-if="item.pdfUrl"
                      icon
                      small
                      :href="item.pdfUrl"
                      target="_blank"
                      title="Open PDF"
                    >
                      <v-icon small color="red darken-1"
                        >mdi-file-pdf-box</v-icon
                      >
                    </v-btn>
                    <v-btn
                      icon
                      small
                      @click.stop="openPaymentEdit(item)"
                      title="Edit Payment"
                    >
                      <v-icon small color="green darken-1">mdi-pencil</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <!-- Pagination -->
            <div class="text-xs-center mt-2" v-if="totalPages > 1">
              <v-pagination
                v-model="currentPage"
                :length="totalPages"
                :total-visible="7"
              ></v-pagination>
            </div>
          </div>
        </material-card>
      </v-flex>
        </v-layout>
      </div>

      <!-- ── Invoice Detail Side Panel ─────────────────────────────────── -->
      <div v-if="detailDialog" class="inv-side-panel">
        <v-card class="inv-side-card">
        <v-card-title class="inv-dialog-header">
          <div>
            <span class="headline">
              Invoice #{{
                selectedInvoice ? selectedInvoice.invoiceNumber : ""
              }}
            </span>
            <div class="caption grey--text mt-1" v-if="selectedInvoice">
              {{
                formatPeriod(
                  selectedInvoice.periodStart,
                  selectedInvoice.periodEnd
                )
              }}
            </div>
          </div>
          <v-spacer></v-spacer>

          <v-btn
            v-if="selectedInvoice && selectedInvoice.pdfUrl"
            small
            color="red darken-1"
            dark
            :href="selectedInvoice.pdfUrl"
            target="_blank"
            title="Open PDF"
            class="inv-header-action inv-header-btn"
          >
            <v-icon small left>mdi-file-pdf-box</v-icon>
            PDF
          </v-btn>
          <v-btn
            v-if="selectedInvoice"
            small
            color="#4caf50"
            dark
            @click="openPaymentEditFromDetail"
            title="Edit Payment"
            class="inv-header-action inv-header-btn"
          >
            <v-icon small left>mdi-pencil</v-icon>
            Payment
          </v-btn>
          <v-btn
            small
            color="grey darken-1"
            dark
            @click="detailDialog = false"
            title="Close"
            class="inv-header-action inv-header-btn"
          >
            <v-icon small left>mdi-close</v-icon>
            Close
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="inv-dialog-body">
          <!-- Loading -->
          <div v-if="detailLoading" class="text-xs-center py-5">
            <v-progress-circular
              indeterminate
              color="#4caf50"
              size="40"
            ></v-progress-circular>
            <div class="mt-2 grey--text">Loading details…</div>
          </div>

          <!-- Error -->
          <div v-else-if="detailError" class="text-xs-center py-4">
            <v-icon large color="error">mdi-alert-circle</v-icon>
            <div class="mt-2 grey--text">{{ detailError }}</div>
            <v-btn color="#4caf50" dark class="mt-2" small @click="reloadDetail"
              >Retry</v-btn
            >
          </div>

          <!-- Content -->
          <template v-else-if="invoiceDetail">
            <!-- Fixed summary section -->
            <div class="inv-detail-fixed">
            <!-- Summary grid -->
            <v-layout row wrap class="mt-3 mb-2">
              <v-flex xs6 sm4 md3>
                <div class="inv-detail-block">
                  <div class="inv-detail-label">Caller</div>
                  <div class="inv-detail-value">
                    {{ getCallerDisplay(invoiceDetail.invoice) }}
                  </div>
                </div>
              </v-flex>
              <v-flex xs6 sm4 md3>
                <div class="inv-detail-block">
                  <div class="inv-detail-label">Questions</div>
                  <div class="inv-detail-value">
                    {{ invoiceDetail.invoice.questionCount }}
                  </div>
                </div>
              </v-flex>
              <v-flex xs6 sm4 md3>
                <div class="inv-detail-block">
                  <div class="inv-detail-label">Twilio Cost</div>
                  <div class="inv-detail-value">
                    {{ formatCurrency(invoiceDetail.invoice.twilioCost) }}
                  </div>
                </div>
              </v-flex>
              <v-flex xs6 sm4 md3>
                <div class="inv-detail-block">
                  <div class="inv-detail-label">API Cost</div>
                  <div class="inv-detail-value">
                    {{ formatCurrency(invoiceDetail.invoice.apiCost) }}
                  </div>
                </div>
              </v-flex>
              <v-flex xs6 sm4 md3>
                <div class="inv-detail-block">
                  <div class="inv-detail-label">Total Cost</div>
                  <div class="inv-detail-value font-weight-bold">
                    {{ formatCurrency(invoiceDetail.invoice.totalCost) }}
                  </div>
                </div>
              </v-flex>
              <v-flex xs6 sm4 md3>
                <div class="inv-detail-block">
                  <div class="inv-detail-label">Status</div>
                  <div class="inv-detail-value">
                    <v-chip
                      small
                      :color="getPayColor(invoiceDetail.invoice)"
                      text-color="white"
                      >{{ getPayLabel(invoiceDetail.invoice) }}</v-chip
                    >
                  </div>
                </div>
              </v-flex>
              <v-flex
                xs6
                sm4
                md3
                v-if="
                  invoiceDetail.invoice.paidAmount !== null &&
                    invoiceDetail.invoice.paidAmount !== undefined
                "
              >
                <div class="inv-detail-block">
                  <div class="inv-detail-label">Paid Amount</div>
                  <div class="inv-detail-value">
                    {{ formatCurrency(invoiceDetail.invoice.paidAmount) }}
                  </div>
                </div>
              </v-flex>
              <v-flex xs6 sm4 md3 v-if="invoiceDetail.invoice.paymentMethod">
                <div class="inv-detail-block">
                  <div class="inv-detail-label">Pay Method</div>
                  <div class="inv-detail-value">
                    {{ invoiceDetail.invoice.paymentMethod }}
                  </div>
                </div>
              </v-flex>
              <v-flex xs6 sm4 md3 v-if="invoiceDetail.invoice.paymentReference">
                <div class="inv-detail-block">
                  <div class="inv-detail-label">Reference</div>
                  <div class="inv-detail-value">
                    {{ invoiceDetail.invoice.paymentReference }}
                  </div>
                </div>
              </v-flex>
              <v-flex xs12 sm8 md6 v-if="invoiceDetail.invoice.emailTo">
                <div class="inv-detail-block">
                  <div class="inv-detail-label">Email To</div>
                  <div class="inv-detail-value">
                    {{ invoiceDetail.invoice.emailTo }}
                  </div>
                </div>
              </v-flex>
            </v-layout>

            <v-divider class="mb-2"></v-divider>
            </div><!-- end inv-detail-fixed -->

            <!-- Scrollable questions section -->
            <div class="inv-questions-section">
            <!-- Questions heading -->
            <div class="subheading font-weight-medium mb-2">
              Questions
              <span class="grey--text caption ml-1"
                >({{ filteredQuestions.length }})</span
              >
            </div>

            <div class="inv-question-filters mb-2">
              <v-select
                v-model="questionTypeFilter"
                :items="questionTypeOptions"
                item-text="text"
                item-value="value"
                hide-details
                dense
                solo
                flat
                label="Type"
                class="inv-q-filter-select inv-q-filter-type"
              ></v-select>

              <v-text-field
                v-model.number="questionMinCost"
                type="number"
                min="0"
                step="0.01"
                hide-details
                dense
                solo
                flat
                clearable
                label="Min $"
                class="inv-q-filter-select inv-q-filter-cost"
              ></v-text-field>

              <v-text-field
                v-model.number="questionMaxCost"
                type="number"
                min="0"
                step="0.01"
                hide-details
                dense
                solo
                flat
                clearable
                label="Max $"
                class="inv-q-filter-select inv-q-filter-cost"
              ></v-text-field>

              <v-menu
                v-model="questionFromDateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="questionFromDate"
                    label="From"
                    prepend-icon="mdi-calendar"
                    readonly
                    clearable
                    hide-details
                    dense
                    solo
                    flat
                    class="inv-q-filter-select inv-q-filter-date"
                    v-on="on"
                    @click:clear="questionFromDate = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="questionFromDate"
                  no-title
                  @input="questionFromDateMenu = false"
                ></v-date-picker>
              </v-menu>

              <v-menu
                v-model="questionToDateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="questionToDate"
                    label="To"
                    prepend-icon="mdi-calendar"
                    readonly
                    clearable
                    hide-details
                    dense
                    solo
                    flat
                    class="inv-q-filter-select inv-q-filter-date"
                    v-on="on"
                    @click:clear="questionToDate = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="questionToDate"
                  no-title
                  @input="questionToDateMenu = false"
                ></v-date-picker>
              </v-menu>

              <v-btn small flat color="grey darken-1" @click="clearQuestionFilters" class="inv-q-clear-btn">
                Clear
              </v-btn>
            </div>

            <div
              v-if="filteredQuestions.length === 0"
              class="text-xs-center grey--text py-3"
            >
              No questions match the current filters.
            </div>

            <div v-else class="inv-questions-wrap">
              <v-data-table
                :headers="questionHeaders"
                :items="filteredQuestions"
                hide-actions
                class="inv-questions-table"
              >
                <template slot="headerCell" slot-scope="{ header }">
                  <span class="font-weight-light" v-text="header.text" />
                </template>
                <template slot="items" slot-scope="{ item }">
                  <tr>
                    <td class="nowrap caption">{{ formatDate(item.date) }}</td>
                    <td class="nowrap">
                      <v-chip
                        small
                        :color="item.type === 'WEB' ? 'blue' : 'teal'"
                        text-color="white"
                        class="type-chip"
                        >{{ item.type }}</v-chip
                      >
                      <v-chip
                        v-if="item.urls"
                        small
                        color="deep-purple"
                        text-color="white"
                        class="type-chip ml-1"
                      >
                        <v-icon small left>mdi-image</v-icon>
                        Pic
                      </v-chip>
                    </td>
                    <td class="q-cell">
                      <div class="q-text">{{ item.question }}</div>
                    </td>
                    <td class="a-cell">
                      <div class="a-text">{{ item.answer }}</div>
                    </td>
                    <td class="text-xs-right caption">
                      {{ formatCurrency(item.twilioCost) }}
                    </td>
                    <td class="text-xs-right caption">
                      {{ formatCurrency(item.apiCost) }}
                    </td>
                    <td class="text-xs-right caption font-weight-medium">
                      {{ formatCurrency(item.totalCost) }}
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </div>
            </div><!-- end inv-questions-section -->
          </template>
        </v-card-text>
      </v-card>
      </div>
    </div>

    <!-- ── Edit Payment Dialog ───────────────────────────────────────── -->
    <v-dialog v-model="paymentDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="headline">
          Edit Payment
          <span class="caption grey--text ml-2" v-if="editInvoice">
            — Invoice #{{ editInvoice.invoiceNumber }}
          </span>
        </v-card-title>
        <v-divider></v-divider>

        <v-card-text class="payment-form-wrap">
          <v-layout row wrap class="payment-form-grid">
            <!-- Paid toggle -->
            <v-flex xs12 class="pb-2">
              <v-switch
                v-model="payForm.paid"
                :label="payForm.paid ? 'Paid' : 'Unpaid'"
                color="green"
                hide-details
              ></v-switch>
            </v-flex>

            <!-- Paid Amount -->
            <v-flex xs12 sm6 class="pb-2">
              <v-text-field
                v-model="payForm.paidAmount"
                label="Paid Amount ($)"
                type="number"
                step="0.01"
                min="0"
                prepend-icon="mdi-cash"
                hide-details
                class="mt-2"
              ></v-text-field>
            </v-flex>

            <!-- Payment Date -->
            <v-flex xs12 sm6 class="pb-2">
              <v-menu
                v-model="payDateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="payForm.paymentDate"
                    label="Payment Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    clearable
                    hide-details
                    class="mt-2"
                    v-on="on"
                    @click:clear="payForm.paymentDate = null"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="payForm.paymentDate"
                  no-title
                  @input="payDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-flex>

            <!-- Payment Method -->
            <v-flex xs12 sm6 class="pb-2">
              <v-text-field
                v-model="payForm.paymentMethod"
                label="Payment Method"
                placeholder="e.g. Credit Card, Check…"
                prepend-icon="mdi-credit-card"
                hide-details
                class="mt-2"
              ></v-text-field>
            </v-flex>

            <!-- Payment Reference -->
            <v-flex xs12 sm6 class="pb-2">
              <v-text-field
                v-model="payForm.paymentReference"
                label="Payment Reference"
                placeholder="e.g. TXN-12345"
                prepend-icon="mdi-tag"
                hide-details
                class="mt-2"
              ></v-text-field>
            </v-flex>

            <!-- Notes -->
            <v-flex xs12 class="pb-1">
              <v-text-field
                v-model="payForm.notes"
                label="Notes"
                placeholder="Optional notes…"
                prepend-icon="mdi-note-text"
                hide-details
                multi-line
                rows="2"
                class="mt-2"
              ></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="#999"
            dark
            @click="paymentDialog = false"
            :disabled="paymentSaving"
          >
            Cancel
          </v-btn>
          <v-btn
            color="green darken-1"
            dark
            @click="savePayment"
            :loading="paymentSaving"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
const API_BASE =
  "https://wercbergerproductions-app-164903695581.us-east1.run.app";

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const ROWS_PER_PAGE = 25;

export default {
  name: "Invoices",

  data() {
    return {
      /* ── Invoice list ── */
      invoices: [],
      loading: false,
      loadError: null,

      /* ── Month filter ── */
      filterYear: null,
      filterMonth: null,

      /* ── Pagination ── */
      currentPage: 1,

      /* ── Month options for select ── */
      monthOptions: MONTH_LABELS.map(function(label, i) {
        return { label: label, value: i + 1 };
      }),

      /* ── Table headers ── */
      tableHeaders: [
        { text: "Invoice #", value: "invoiceNumber", sortable: true },
        { text: "Caller", value: "callerId", sortable: false },
        { text: "Period", value: "periodStart", sortable: false },
        { text: "Questions", value: "questionCount", sortable: true },
        { text: "Twilio", value: "twilioCost", sortable: true },
        { text: "API", value: "apiCost", sortable: true },
        { text: "Total", value: "totalCost", sortable: true },
        { text: "Status", value: "paid", sortable: false },
        { text: "Paid Amt", value: "paidAmount", sortable: true },
        { text: "Actions", value: "actions", sortable: false },
      ],

      /* ── Question table headers ── */
      questionHeaders: [
        { text: "Date", value: "date", sortable: true },
        { text: "Type", value: "type", sortable: false },
        { text: "Question", value: "question", sortable: false },
        { text: "Answer", value: "answer", sortable: false },
        { text: "Twilio", value: "twilioCost", sortable: true },
        { text: "API", value: "apiCost", sortable: true },
        { text: "Total", value: "totalCost", sortable: true },
      ],
      questionTypeOptions: [
        { text: "All", value: "All" },
        { text: "Text", value: "TEXT" },
        { text: "Picture", value: "PICTURE" },
      ],
      questionSearch: "",
      questionTypeFilter: "All",
      questionMinCost: null,
      questionMaxCost: null,
      questionFromDate: null,
      questionToDate: null,
      questionFromDateMenu: false,
      questionToDateMenu: false,

      /* ── Detail dialog ── */
      detailDialog: false,
      selectedInvoice: null,
      invoiceDetail: null,
      detailLoading: false,
      detailError: null,

      /* ── Payment edit dialog ── */
      paymentDialog: false,
      editInvoice: null,
      payForm: {
        paid: false,
        paidAmount: null,
        paymentDate: null,
        paymentMethod: null,
        paymentReference: null,
        notes: null,
      },
      payDateMenu: false,
      paymentSaving: false,

      /* ── Snackbar ── */
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      callerNameMap: {},
    };
  },

  computed: {
    availableYears: function() {
      var years = {};
      var now = new Date();
      years[now.getFullYear()] = true;
      years[now.getFullYear() - 1] = true;
      this.invoices.forEach(function(inv) {
        if (inv.periodStart) {
          years[new Date(inv.periodStart).getFullYear()] = true;
        }
      });
      return Object.keys(years)
        .map(Number)
        .sort(function(a, b) {
          return b - a;
        });
    },

    filteredInvoices: function() {
      var vm = this;
      if (!vm.filterYear && !vm.filterMonth) {
        return vm.invoices;
      }
      return vm.invoices.filter(function(inv) {
        var d = inv.periodStart
          ? new Date(inv.periodStart)
          : inv.periodEnd
          ? new Date(inv.periodEnd)
          : null;
        if (!d) return false;
        if (vm.filterYear && d.getFullYear() !== vm.filterYear) return false;
        if (vm.filterMonth && d.getMonth() + 1 !== vm.filterMonth) return false;
        return true;
      });
    },

    kpis: function() {
      var list = this.filteredInvoices;
      var count = list.length;
      var twilio = 0,
        api = 0,
        total = 0,
        paid = 0;
      list.forEach(function(inv) {
        twilio += inv.twilioCost || 0;
        api += inv.apiCost || 0;
        total += inv.totalCost || 0;
        paid += inv.paidAmount || 0;
      });
      return {
        count: count,
        twilio: twilio,
        api: api,
        total: total,
        paid: paid,
        outstanding: total - paid,
      };
    },

    totalPages: function() {
      return Math.ceil(this.filteredInvoices.length / ROWS_PER_PAGE) || 0;
    },

    paginatedInvoices: function() {
      var start = (this.currentPage - 1) * ROWS_PER_PAGE;
      return this.filteredInvoices.slice(start, start + ROWS_PER_PAGE);
    },

    filteredQuestions: function() {
      if (!this.invoiceDetail || !Array.isArray(this.invoiceDetail.questions)) {
        return [];
      }

      var vm = this;
      return this.invoiceDetail.questions.filter(function(q) {
        if (vm.questionTypeFilter && vm.questionTypeFilter !== "All") {
          if (vm.questionTypeFilter === "PICTURE") {
            if (!q || !q.urls) {
              return false;
            }
          } else {
            var qType = q && q.type ? ("" + q.type).toUpperCase() : "";
            if (qType !== vm.questionTypeFilter) {
              return false;
            }
          }
        }

        if (vm.questionMinCost !== null && vm.questionMinCost !== "" && vm.questionMinCost !== undefined) {
          var cost = q && q.totalCost ? q.totalCost : 0;
          if (cost < parseFloat(vm.questionMinCost)) {
            return false;
          }
        }

        if (vm.questionMaxCost !== null && vm.questionMaxCost !== "" && vm.questionMaxCost !== undefined) {
          var costMax = q && q.totalCost ? q.totalCost : 0;
          if (costMax > parseFloat(vm.questionMaxCost)) {
            return false;
          }
        }

        if (vm.questionFromDate || vm.questionToDate) {
          if (!q || !q.date) {
            return false;
          }

          var qDate = vm.toDateOnlyString(q.date);
          if (!qDate) {
            return false;
          }

          if (vm.questionFromDate && qDate < vm.questionFromDate) {
            return false;
          }
          if (vm.questionToDate && qDate > vm.questionToDate) {
            return false;
          }
        }

        return true;
      });
    },
  },

  watch: {
    /* Reset to page 1 whenever filter changes */
    filteredInvoices: function() {
      this.currentPage = 1;
    },
  },

  created: function() {
    this.loadInvoices();
  },

  methods: {
    /* ── API calls ─────────────────────────────────────────────────── */

    loadInvoices: async function() {
      this.loading = true;
      this.loadError = null;
      try {
        var response = await this.$http.get(
          API_BASE + "/api/aiInvoices/getInvoices"
        );
        var fetchedInvoices = Array.isArray(response.data) ? response.data : [];
        this.invoices = fetchedInvoices.sort(function(a, b) {
          var aTotal = a && a.totalCost ? parseFloat(a.totalCost) : 0;
          var bTotal = b && b.totalCost ? parseFloat(b.totalCost) : 0;
          aTotal = isNaN(aTotal) ? 0 : aTotal;
          bTotal = isNaN(bTotal) ? 0 : bTotal;
          return bTotal - aTotal;
        });

        await this.loadCallerNameMap();

        /* Auto-select the latest month that has generated invoices on first load */
        if (this.invoices.length && this.filterYear === null) {
          this.applyLatestInvoiceMonthFilter();
        }
      } catch (err) {
        this.loadError = "Could not load invoices. Please try again.";
        console.error("loadInvoices error", err);
      } finally {
        this.loading = false;
      }
    },

    loadDetail: async function(invoice) {
      this.invoiceDetail = null;
      this.detailError = null;
      this.detailLoading = true;
      try {
        var response = await this.$http.get(
          API_BASE + "/api/aiInvoices/getInvoiceQuestions",
          { params: { invoiceId: invoice.id } }
        );
        this.invoiceDetail = response.data;
      } catch (err) {
        this.detailError = "Could not load invoice questions.";
        console.error("getInvoiceQuestions error", err);
      } finally {
        this.detailLoading = false;
      }
    },

    savePayment: async function() {
      if (!this.editInvoice) return;
      this.paymentSaving = true;
      try {
        var f = this.payForm;
        var paidAmt =
          f.paidAmount !== null &&
          f.paidAmount !== "" &&
          f.paidAmount !== undefined
            ? parseFloat(f.paidAmount)
            : null;

        await this.$http.post(API_BASE + "/api/aiInvoices/updatePaymentInfo", {
          invoiceId: this.editInvoice.id,
          paid: f.paid,
          paidAmount: paidAmt,
          paymentDate: f.paymentDate ? f.paymentDate + "T00:00:00" : null,
          paymentMethod: f.paymentMethod || null,
          paymentReference: f.paymentReference || null,
          notes: f.notes || null,
        });

        this.showSnackbar("Payment updated successfully.", "success");
        this.paymentDialog = false;
        await this.loadInvoices();
      } catch (err) {
        this.showSnackbar("Failed to save payment. Please try again.", "error");
        console.error("updatePaymentInfo error", err);
      } finally {
        this.paymentSaving = false;
      }
    },

    /* ── Dialog helpers ─────────────────────────────────────────────── */

    loadCallerNameMap: async function() {
      try {
        var response = await this.$http.get(
          API_BASE + "/api/openai/getChatSidebar"
        );
        var sidebarItems = Array.isArray(response.data) ? response.data : [];
        var nextMap = {};

        sidebarItems.forEach(function(chat) {
          if (!chat || !chat.callerId || !chat.name) {
            return;
          }

          var key = this.normalizeCallerKey(chat.callerId);
          if (!key) {
            return;
          }

          if (!nextMap[key]) {
            nextMap[key] = chat.name;
          }
        }, this);

        this.callerNameMap = nextMap;
      } catch (error) {
        console.error("loadCallerNameMap error", error);
      }
    },

    openDetail: function(invoice) {
      this.selectedInvoice = invoice;
      this.detailDialog = true;
      this.clearQuestionFilters();
      this.loadDetail(invoice);
    },

    reloadDetail: function() {
      if (this.selectedInvoice) {
        this.loadDetail(this.selectedInvoice);
      }
    },

    openPaymentEdit: function(invoice) {
      this.editInvoice = invoice;
      this.payForm = {
        paid: invoice.paid || false,
        paidAmount:
          invoice.paidAmount !== null && invoice.paidAmount !== undefined
            ? invoice.paidAmount
            : null,
        paymentDate: invoice.paymentDate
          ? invoice.paymentDate.substring(0, 10)
          : null,
        paymentMethod: invoice.paymentMethod || null,
        paymentReference: invoice.paymentReference || null,
        notes: invoice.notes || null,
      };
      this.paymentDialog = true;
    },

    openPaymentEditFromDetail: function() {
      this.openPaymentEdit(this.selectedInvoice);
    },

    /* ── Filter helpers ─────────────────────────────────────────────── */

    clearFilter: function() {
      this.filterYear = null;
      this.filterMonth = null;
    },

    clearQuestionFilters: function() {
      this.questionSearch = "";
      this.questionTypeFilter = "All";
      this.questionMinCost = null;
      this.questionMaxCost = null;
      this.questionFromDate = null;
      this.questionToDate = null;
    },

    applyLatestInvoiceMonthFilter: function() {
      var latestDate = null;

      this.invoices.forEach(function(inv) {
        var candidate = inv.periodStart ? new Date(inv.periodStart) : null;
        if ((!candidate || isNaN(candidate.getTime())) && inv.periodEnd) {
          candidate = new Date(inv.periodEnd);
        }

        if (!candidate || isNaN(candidate.getTime())) {
          return;
        }

        if (!latestDate || candidate.getTime() > latestDate.getTime()) {
          latestDate = candidate;
        }
      });

      if (latestDate) {
        this.filterYear = latestDate.getFullYear();
        this.filterMonth = latestDate.getMonth() + 1;
      }
    },

    /* ── Formatting helpers ─────────────────────────────────────────── */

    formatCurrency: function(value) {
      if (value === null || value === undefined) return "$0.00";
      return "$" + parseFloat(value).toFixed(2);
    },

    formatPhone: function(phone) {
      if (!phone) return "—";
      var cleaned = ("" + phone).replace(/\D/g, "");
      if (cleaned.length === 10) {
        var m = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        if (m) return "(" + m[1] + ") " + m[2] + "-" + m[3];
      }
      return phone;
    },

    normalizeCallerKey: function(callerId) {
      if (!callerId && callerId !== 0) {
        return "";
      }

      var raw = ("" + callerId).trim();
      if (!raw) {
        return "";
      }

      var digits = raw.replace(/\D/g, "");
      if (digits.length === 11 && digits.charAt(0) === "1") {
        digits = digits.substring(1);
      }
      if (digits.length > 10) {
        digits = digits.substring(digits.length - 10);
      }
      if (digits.length === 10) {
        return digits;
      }

      return raw.toLowerCase();
    },

    getCallerName: function(callerId) {
      var key = this.normalizeCallerKey(callerId);
      if (!key) {
        return "";
      }
      return this.callerNameMap[key] ? this.callerNameMap[key] : "";
    },

    getCallerDisplay: function(invoice) {
      var callerId = invoice && invoice.callerId ? invoice.callerId : "";
      var callerName = this.getCallerName(callerId);
      var formattedPhone = this.formatPhone(callerId);

      if (callerName && formattedPhone && callerName !== formattedPhone) {
        return callerName + " - " + formattedPhone;
      }

      if (callerName) {
        return callerName;
      }

      return formattedPhone;
    },

    formatDate: function(dateStr) {
      if (!dateStr) return "—";
      var d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.getMonth() + 1 + "/" + d.getDate() + "/" + d.getFullYear();
    },

    toDateOnlyString: function(dateInput) {
      if (!dateInput) return null;
      var d = new Date(dateInput);
      if (isNaN(d.getTime())) return null;
      var m = d.getMonth() + 1;
      var day = d.getDate();
      var mm = m < 10 ? "0" + m : "" + m;
      var dd = day < 10 ? "0" + day : "" + day;
      return d.getFullYear() + "-" + mm + "-" + dd;
    },

    formatPeriod: function(start, end) {
      var s = start ? this.formatDate(start) : null;
      var e = end ? this.formatDate(end) : null;
      if (s && e) return s + " – " + e;
      if (s) return s;
      if (e) return e;
      return "—";
    },

    /* ── Payment status badge ───────────────────────────────────────── */

    getPayLabel: function(invoice) {
      if (!invoice) return "Unknown";
      if (invoice.paid) {
        var total = invoice.totalCost || 0;
        var paid = invoice.paidAmount;
        if (
          paid !== null &&
          paid !== undefined &&
          parseFloat(paid) < parseFloat(total) - 0.005
        ) {
          return "Partial";
        }
        return "Paid";
      }
      return "Unpaid";
    },

    getPayColor: function(invoice) {
      var label = this.getPayLabel(invoice);
      if (label === "Paid") return "green darken-1";
      if (label === "Partial") return "orange darken-1";
      return "red darken-1";
    },

    /* ── Toast ──────────────────────────────────────────────────────── */

    showSnackbar: function(message, color) {
      this.snackbar.message = message;
      this.snackbar.color = color || "success";
      this.snackbar.show = true;
    },
  },
};
</script>

<style>
/* ── Page header ─────────────────────────────────────────────────────── */
.inv-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 4px;
}

.inv-title {
  font-size: 22px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
  line-height: 1.3;
}

.inv-subtitle {
  font-size: 13px;
  color: #78909c;
  margin: 2px 0 0;
}

/* ── Filter bar ──────────────────────────────────────────────────────── */
.inv-filter-bar {
  border-radius: 8px !important;
  background: #fafafa !important;
}

.inv-filter-select {
  padding-top: 0 !important;
  margin-top: 0 !important;
}

.inv-result-count {
  font-size: 13px;
  font-weight: 600;
  color: #546e7a;
}

/* ── KPI cards ───────────────────────────────────────────────────────── */
.kpi-card {
  border-radius: 10px;
  padding: 14px 10px 12px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  min-height: 100px;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
  margin-bottom: 4px;
  transition: transform 0.15s;
  overflow: hidden;
  width: 100%;
}

.kpi-card:hover {
  transform: translateY(-2px);
}

.kpi-icon {
  margin-bottom: 4px;
  font-size: 24px !important;
  flex-shrink: 0;
}

.kpi-value {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.5px;
  line-height: 1.2;
  word-break: break-all;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpi-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  opacity: 0.88;
  margin-top: 3px;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpi-blue {
  background: linear-gradient(135deg, #43a047, #66bb6a);
}
.kpi-purple {
  background: linear-gradient(135deg, #6a1b9a, #ab47bc);
}
.kpi-teal {
  background: linear-gradient(135deg, #00695c, #26a69a);
}
.kpi-orange {
  background: linear-gradient(135deg, #e65100, #fb8c00);
}
.kpi-green {
  background: linear-gradient(135deg, #3949ab, #5c6bc0);
}
.kpi-red {
  background: linear-gradient(135deg, #b71c1c, #ef5350);
}

/* Skeleton KPI */
.kpi-skeleton {
  background: #e0e0e0 !important;
  box-shadow: none !important;
}

.skel-icon,
.skel-val,
.skel-lbl {
  background: #bdbdbd;
  border-radius: 4px;
  animation: shimmer 1.4s infinite ease-in-out;
}

.skel-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  margin-bottom: 8px;
}
.skel-val {
  width: 70%;
  height: 20px;
  margin-bottom: 8px;
}
.skel-lbl {
  width: 50%;
  height: 12px;
}

@keyframes shimmer {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.45;
  }
  100% {
    opacity: 1;
  }
}

/* ── Invoice table ───────────────────────────────────────────────────── */
.inv-table tbody tr:nth-child(even) {
  background-color: #f5f7fa;
}

.inv-row:hover {
  background-color: #e8f4fd !important;
  cursor: default;
}

.nowrap {
  white-space: nowrap;
}

.pay-chip {
  font-size: 11px !important;
  height: 20px !important;
}

/* ── Detail side panel ──────────────────────────────────────────────── */
.invoices-page {
  min-height: calc(100vh - 64px);
  display: flex !important;
  flex-direction: column;
}

.inv-page-shell {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.inv-main-content {
  flex: 1 1 auto;
  min-width: 0;
}

.inv-dialog-header {
  background: #f8f9fb;
  position: sticky;
  top: 0;
  z-index: 2;
}

.inv-header-action {
  margin-left: 4px;
}

.inv-header-btn {
  height: 30px !important;
  min-width: 0 !important;
  padding: 0 10px !important;
  font-size: 11px !important;
  font-weight: 600;
  text-transform: none;
}

.inv-dialog-body {
  flex: 1 1 auto;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0 24px 0 !important;
}

.inv-detail-fixed {
  flex: 0 0 auto;
  padding-top: 12px;
}

.inv-questions-section {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.inv-side-panel {
  flex: 0 0 60vw;
  width: 60vw;
  min-width: 560px;
  max-width: 1080px;
  position: sticky;
  top: 10px;
  height: calc(100vh - 14px);
  align-self: flex-start;
  margin-top: 10px;
}

.inv-side-card {
  height: 100%;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.inv-side-card >>> .v-card__text {
  flex: 1 1 auto !important;
  min-height: 0 !important;
}

.inv-detail-block {
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 6px;
  margin: 4px 4px 8px;
}

.inv-detail-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: #90a4ae;
  margin-bottom: 2px;
}

.inv-detail-value {
  font-size: 14px;
  color: #37474f;
  font-weight: 500;
  word-break: break-word;
}

/* ── Questions table ─────────────────────────────────────────────────── */
.inv-questions-wrap {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: auto;
  min-height: 0;
}

.inv-question-filters {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
  border: 1px solid #eceff1;
  border-radius: 8px;
  background: #fafcfd;
  padding: 6px 10px;
  flex-shrink: 0;
}

.inv-q-filter-select {
  flex: 1 1 0;
  min-width: 0;
}

.inv-q-filter-type {
  flex: 0 0 100px;
}

.inv-q-filter-cost {
  flex: 0 0 115px;
}

.inv-q-filter-date {
  flex: 1 1 80px;
  min-width: 0;
}

.inv-q-clear-btn {
  flex-shrink: 0;
}

.inv-q-filter-select >>> .v-input__slot {
  box-shadow: none !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 6px !important;
  min-height: 32px !important;
  padding: 0 8px !important;
}

.inv-q-filter-select >>> .v-input__control {
  min-height: 32px !important;
}

.inv-questions-table tbody tr:nth-child(even) {
  background-color: #f5f7fa;
}

.type-chip {
  font-size: 10px !important;
  height: 18px !important;
}

.q-cell,
.a-cell {
  min-width: 160px;
  max-width: 260px;
}

.q-text,
.a-text {
  max-height: 60px;
  overflow: hidden;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  font-size: 13px;
  line-height: 1.45;
}

/* ── Date picker button color fix (shared with other views) ──────────── */
.v-date-picker-table .v-btn {
  color: rgba(0, 0, 0, 0.87) !important;
}

/* ── Payment dialog spacing ──────────────────────────────────────────── */
.payment-form-wrap {
  padding-top: 14px !important;
}

.payment-form-grid .v-input {
  margin-top: 8px;
}

.payment-form-grid .v-input__slot {
  margin-bottom: 0 !important;
}

@media (max-width: 1264px) {
  .inv-side-panel {
    flex-basis: 64vw;
    width: 64vw;
    min-width: 500px;
  }
}

@media (max-width: 960px) {
  .inv-page-shell {
    display: block;
  }

  .inv-side-panel {
    position: static;
    width: 100%;
    min-width: 0;
    max-width: none;
    height: auto;
    margin-top: 12px;
  }

  .inv-side-card {
    height: auto;
    min-height: 420px;
  }

  .inv-dialog-body {
    max-height: 70vh;
    overflow-y: auto;
    display: block;
  }

  .inv-questions-section {
    display: block;
    overflow: visible;
  }

  .inv-questions-wrap {
    overflow-y: visible;
  }
}
</style>
